// due to use of snake case mutation
'use client';

import { Warning as WarningIcon } from '@mui/icons-material';
import {
	Button,
	CircularProgress,
	FormControl,
	FormControlLabel,
	FormGroup,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	TextField,
	Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useApolloClient, useMutation } from '@parkchirp/api-client';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';

import { REASSIGN_PRIMARY_CONTACT } from '../../mutations';
import { GET_USER_FOR_EMAIL } from '../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../types';
import { isValidEmail } from '../../utils';
import { EmailsInput, StateSelect } from '../inputs';
import { EditorModal } from './editorModal';
import { SimpleModal } from './simpleModal';

interface ReassignPrimaryContactModalProps {
	handleClose: (shouldRefetchSubscriptions: boolean) => void;
	handleConfirmationModalClose: (emailDefaultValue?: string) => void;
	payorId: string;
	payorCognitoSub: string;
	payorWordpressUserId: number;
	oldPayorAttention: string;
	oldPayorStreet: string;
	oldPayorCity: string;
	oldPayorState: string;
	oldPayorZip: string;
	oldBillingCompanyName: string;
	oldBillingAddressStreet2: string;
	oldBillingCity: string;
	oldBillingState: string;
	oldBillingStreet: string;
	oldBillingZip: string;
	setShowSnackbar: SetShowSnackbar;
	setShowDialog: SetShowDialog;
	emailDefaultValue?: string;
}

export const ReassignPrimaryContactModal = ({
	handleClose,
	payorId,
	payorCognitoSub,
	payorWordpressUserId,
	oldPayorAttention,
	oldPayorCity,
	oldPayorState,
	oldPayorStreet,
	oldPayorZip,
	oldBillingCompanyName,
	oldBillingAddressStreet2,
	oldBillingCity,
	oldBillingState,
	oldBillingStreet,
	oldBillingZip,
	setShowSnackbar,
	setShowDialog,
	handleConfirmationModalClose,
	emailDefaultValue = null,
}: ReassignPrimaryContactModalProps) => {
	const theme = useTheme();
	const [loading, setLoading] = useState<boolean>(false);
	const [email, setEmail] = useState<string | null>(emailDefaultValue ?? '');
	const [reassignmentType, setReassignmentType] = useState<string | null>('SUBSCRIPTION');
	const [invoiceRecipients, setInvoiceRecipients] = useState<string[]>([]);

	const [reassignPrimaryContact] = useMutation(REASSIGN_PRIMARY_CONTACT, {
		context: {
			apiVersion: 'v2',
		},
	});
	const client = useApolloClient();

	const router = useRouter();
	const { subscription: subscriptionId } = router.query;
	// primary contact address state
	const [billingAddressIsSameAsPayorAddress, setBillingAddressIsSameAsPayorAddress] = useState<boolean>(false);
	const [payorAttn, setPayorAttn] = useState<string>(oldPayorAttention);
	const [payorAddress, setPayorAddress] = useState<string>(oldPayorStreet);
	const [payorCity, setPayorCity] = useState<string>(oldPayorCity);
	const [payorState, setPayorState] = useState<string>(oldPayorState);
	const [payorZip, setPayorZip] = useState<string>(oldPayorZip);

	// billing address state
	const [billingCompanyName, setBillingCompanyName] = useState<string>(oldBillingCompanyName);
	const [billingAddress1, setBillingAddress1] = useState<string>(oldBillingStreet);
	const [billingAddress2, setBillingAddress2] = useState<string>(oldBillingAddressStreet2);
	const [billingCity, setBillingCity] = useState<string>(oldBillingCity);
	const [billingState, setBillingState] = useState<string>(oldBillingState);
	const [billingZip, setBillingZip] = useState<string>(oldBillingZip);

	const isSubscriptionReassignment = useMemo(() => reassignmentType === 'SUBSCRIPTION', [reassignmentType]);

	const findUserForEmail = useCallback(async () => {
		const { data } = await client.query({
			query: GET_USER_FOR_EMAIL,
			variables: { email },
		});

		return {
			newCognitoSub: data?.getUserForEmail.cognitoSub,
			newWordpressUserId: data?.getUserForEmail.wordpressUserId,
		};
	}, [client, email]);

	const doReassign = useCallback(
		async ({ newCognitoSub = null, newWordpressUserId = null }) => {
			await reassignPrimaryContact({
				variables: {
					level: reassignmentType,
					payor: {
						id: payorId,
						cognitoSub: payorCognitoSub,
						wordpressUserId: payorWordpressUserId,
						billingAttn: payorAttn,
						infoStreet: payorAddress,
						infoCity: payorCity,
						infoState: payorState,
						infoZip: payorZip,
						billingCompanyName,
						billingStreet: billingAddress1,
						billingAddressStreet2: billingAddress2,
						billingCity,
						billingState,
						billingZip,
					},
					subscriptionId,
					newEmail: email,
					newCognitoSub,
					newWordpressUserId,
					invoiceRecipients,
				},
				onError: (apolloError) => {
					setShowSnackbar({
						message: apolloError.message,
						severity: 'error',
					});
				},
				onCompleted: () => {
					setShowSnackbar({
						message: 'Email address has been updated.',
						severity: 'success',
					});

					handleClose(true);
				},
			});
		},
		[
			email,
			handleClose,
			payorCognitoSub,
			payorId,
			payorWordpressUserId,
			reassignPrimaryContact,
			reassignmentType,
			setShowSnackbar,
			subscriptionId,
			billingZip,
			billingAddress1,
			billingAddress2,
			billingCity,
			billingCompanyName,
			payorCity,
			payorState,
			payorAddress,
			billingState,
			payorAttn,
			payorZip,
			invoiceRecipients,
		],
	);

	const hasAllRequiredFields = useCallback(() => {
		const requiredFields = [{ label: 'Email', value: email }];

		const additionalValidations = [
			{
				message: `Invalid email address`,
				failingCondition: email && !isValidEmail(email),
			},
		];

		if (isSubscriptionReassignment) {
			additionalValidations.push({
				message: `Please enter a valid zip code which is
                formatted as either five digits (xxxxx) or nine digits with a hyphen (xxxxx-xxxx)`,
				failingCondition:
					(payorZip && !/^\d{5}$|^\d{5}-\d{4}$/.test(payorZip)) ||
					(billingZip && !/^\d{5}$|^\d{5}-\d{4}$/.test(billingZip)),
			});
			requiredFields.push(
				{ label: 'Payor Address 1', value: payorAddress },
				{ label: 'Payor City', value: payorCity },
				{ label: 'Payor State', value: payorState },
				{ label: 'Payor Zip', value: payorZip },

				{ label: 'Billing Address 1', value: billingAddress1 },
				{ label: 'Billing City', value: billingCity },
				{ label: 'Billing State', value: billingState },
				{ label: 'Billing Zip', value: billingZip },
			);
		}
		const missingFields = requiredFields.filter(({ value }) => ['', null, undefined].includes(value));
		if (missingFields.length) {
			const missingAsString = missingFields?.map(({ label }) => label).join(', ');
			setShowSnackbar({
				message: `Unable to save due to missing field(s): ${missingAsString}`,
				severity: 'error',
				autoHideDuration: 6000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}

		const failingValidations = additionalValidations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				autoHideDuration: 6000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [
		email,
		payorZip,
		billingZip,
		payorAddress,
		payorCity,
		payorState,
		billingAddress1,
		billingCity,
		billingState,
		setShowSnackbar,
		isSubscriptionReassignment,
	]);

	const showConfirmModal = useCallback(async () => {
		if (hasAllRequiredFields()) {
			const { newCognitoSub, newWordpressUserId } = await findUserForEmail();
			const reassignConfirmationModal = (
				<SimpleModal
					content={`
                        Reassign primary contact to ${email}?`}
					handleDismiss={() => handleConfirmationModalClose(email)}
					handleConfirm={() => {
						doReassign({ newCognitoSub, newWordpressUserId });
					}}
					dismissText='Cancel'
					confirmText='Reassign'
				/>
			);
			const createNewAccountConfirmationModal = (
				<SimpleModal
					content={`
            No account found for ${email}. A new account will be created, do you want to continue?`}
					handleDismiss={() => handleConfirmationModalClose(email)}
					handleConfirm={() => {
						doReassign({});
					}}
					dismissText='Cancel'
					confirmText='Confirm'
				/>
			);
			setLoading(true);

			setLoading(false);
			if (newCognitoSub || newWordpressUserId) {
				return setShowDialog(reassignConfirmationModal);
			}
			return setShowDialog(createNewAccountConfirmationModal);
		}
		return false;
	}, [hasAllRequiredFields, findUserForEmail, setShowDialog, doReassign, email, handleConfirmationModalClose]);

	return (
		<EditorModal
			handleClose={() => handleClose(false)}
			handleSave={null}
			loading={false}
			containerClassName='reassignPrimaryContactModal'
			headerText='Reassign Primary Contact'
			theme={theme}
		>
			<p style={{ marginBottom: '10px' }}>Enter the email address of the new primary contact</p>
			<Grid container spacing={2} marginBottom='10px'>
				<Grid item xs={12}>
					<TextField
						id='reassign-primary-contact-email'
						fullWidth
						required
						label='Email'
						type='email'
						variant='outlined'
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						inputProps={{ 'data-cy': 'reassign-email-input' }}
					/>
				</Grid>
				<Grid item xs={12}>
					<FormControl fullWidth required>
						<InputLabel id='reassignment-label'>Operation</InputLabel>
						<Select
							labelId='reassignment-label'
							id='reassignment'
							label='Operation'
							value={reassignmentType}
							onChange={(e) => setReassignmentType(String(e.target.value))}
							data-cy='reassign-operation-select-menu'
						>
							<MenuItem value='SUBSCRIPTION'>Reassign current subscription</MenuItem>
							<MenuItem value='PAYOR'>Reassign current subscriber</MenuItem>
							<MenuItem value='USER'>Reassign all subscribers</MenuItem>
						</Select>
						{isSubscriptionReassignment && (
							<p style={{ fontSize: 14 }}>
								Reassigning the Current Subscription will only change the Primary Contact for this
								single account. Please enter the new Primary Contact&apos;s information below.
							</p>
						)}
						{reassignmentType === 'PAYOR' && (
							<p style={{ fontSize: 14 }}>
								Reassigning the Current Subscriber will change the Primary Contact for all accounts that
								belong to this account&apos;s Subscriber ID. Typically this is every account that shares
								the original Primary Contact email address but not always.
							</p>
						)}
						{reassignmentType === 'USER' && (
							<p style={{ fontSize: 14 }}>
								Reassigning all Subscribers will change the Primary Contact for every account that
								shares the original Primary Contact email.
							</p>
						)}
					</FormControl>
				</Grid>
				{isSubscriptionReassignment && (
					<>
						<Grid item xs={12}>
							<TextField
								id='name'
								label='Payor Attention'
								fullWidth
								value={payorAttn || ''}
								onChange={(e) => setPayorAttn(e.target.value)}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								required
								id='name'
								label='Payor Address'
								fullWidth
								value={payorAddress || ''}
								onChange={(e) => setPayorAddress(e.target.value)}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								required
								id='name'
								label='Payor City'
								fullWidth
								value={payorCity || ''}
								onChange={(e) => setPayorCity(e.target.value)}
							/>
						</Grid>
						<Grid item xs={12}>
							<StateSelect
								label='Payor State'
								value={payorState || ''}
								onChange={(e) => setPayorState(e.target.value)}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								required
								id='name'
								label='Payor Zip'
								fullWidth
								value={payorZip || ''}
								onChange={(e) => setPayorZip(e.target.value)}
							/>
						</Grid>

						<Grid item xs={3}>
							<FormGroup>
								<FormControlLabel
									control={
										<Switch
											onChange={(event) => {
												setBillingAddressIsSameAsPayorAddress(event.target.checked);
												if (event.target.checked) {
													setBillingCompanyName(payorAttn);
													setBillingAddress1(payorAddress);
													setBillingAddress2('');
													setBillingCity(payorCity);
													setBillingState(payorState);
													setBillingZip(payorZip);
												} else {
													setBillingCompanyName('');
													setBillingAddress1('');
													setBillingAddress2('');
													setBillingCity('');
													setBillingState('');
													setBillingZip('');
												}
											}}
											checked={billingAddressIsSameAsPayorAddress}
											data-cy='billing-address-switch'
										/>
									}
									label='Billing Address is same as payor address'
								/>
							</FormGroup>
						</Grid>
						{!billingAddressIsSameAsPayorAddress ? (
							<>
								<Grid item xs={12}>
									<TextField
										id='name'
										name='name'
										label='Billing Name (Company Name)'
										fullWidth
										value={billingCompanyName}
										onChange={(e) => setBillingCompanyName(e.target.value)}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										required
										id='name'
										label='Billing Address 1'
										fullWidth
										value={billingAddress1 || ''}
										onChange={(e) => setBillingAddress1(e.target.value)}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										id='name'
										label='Billing Address 2'
										fullWidth
										value={billingAddress2 || ''}
										onChange={(e) => setBillingAddress2(e.target.value)}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										required
										id='name'
										label='Billing City'
										fullWidth
										value={billingCity || ''}
										onChange={(e) => setBillingCity(e.target.value)}
									/>
								</Grid>
								<Grid item xs={12}>
									<StateSelect
										label='Billing State'
										value={billingState || ''}
										onChange={(e) => setBillingState(e.target.value)}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										required
										id='name'
										label='Billing Zip'
										fullWidth
										value={billingZip || ''}
										onChange={(e) => setBillingZip(e.target.value)}
									/>
								</Grid>
							</>
						) : null}
						{isSubscriptionReassignment && (
							<Grid item xs={12}>
								<Stack direction='row' alignItems='center' gap={1} mt={1} mb={2}>
									<WarningIcon color='warning' />
									<Typography sx={{ fontSize: 14 }}>
										Reassigning the Current Subscription will remove existing Invoice Email
										Recipients. If there are recipients you wish to add, please add them below.
									</Typography>
								</Stack>
								<EmailsInput
									emailAddresses={invoiceRecipients}
									setEmailAddresses={setInvoiceRecipients}
									label='Enter additional recipient emails here'
								/>
							</Grid>
						)}
					</>
				)}
				<Grid item xs={12} display='flex' justifyContent='left'>
					<Stack direction='row' alignItems='center' gap={1} mt={1} mb={2}>
						<WarningIcon color='warning' />
						<Typography sx={{ fontSize: 14 }}>
							Reassigning the Primary Contact will remove Recurring Payment configurations. Please
							reconfigure Recurring Payments after completing the Reassign Primary Contact action if
							desired.
						</Typography>
					</Stack>
				</Grid>
				<Grid item xs={12} display='flex' justifyContent='right'>
					<Button
						variant='contained'
						disabled={!email || loading}
						onClick={showConfirmModal}
						data-cy='submit-button'
					>
						{loading ? (
							<CircularProgress color='inherit' size={20} data-cy='loading-indicator' />
						) : (
							'Submit'
						)}
					</Button>
				</Grid>
			</Grid>
		</EditorModal>
	);
};
